import React, { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Snackbar,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const DELETE_SIM_MUTATION = gql`
  mutation DeleteSim($simId: String!) {
    deleteSim(sim_id: $simId)
  }
`;

interface DeleteSimDialogProps {
  open: boolean;
  onClose: () => void;
  device: {
    imei?: string;
    serial?: string;
    iccid?: string;
    sim_id?: string;
    [key: string]: any;
  };
  onDeleted?: () => void;
}

const DeleteSimDialog: React.FC<DeleteSimDialogProps> = ({ 
  open, 
  onClose, 
  device,
  onDeleted
}) => {
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [deleteSim, { loading }] = useMutation(DELETE_SIM_MUTATION);

  const handleDelete = async () => {
    if (!device?.sim_id) return;
    try {
      await deleteSim({ 
        variables: { simId: device.sim_id },
        update(cache) {
          cache.evict({ id: `Sim:${device.sim_id}` });
          cache.gc();
        }
      });
      setSnackbar({ message: 'SIM deleted successfully', severity: 'success' });
      onDeleted?.();
      onClose();
    } catch (err) {
      const errorMessage =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message)
          : 'An error occurred';
      setSnackbar({ message: errorMessage, severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => setSnackbar(null);


  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle sx={{ pt: '5%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Are you sure you want to delete this sim?</span>
          <IconButton aria-label="close" onClick={onClose} size="small" sx={{ color: 'inherit', right: -15, top: -20 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogActions sx={{ pb: '5%', px: '5%', justifyContent: 'center', gap: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbar?.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        ContentProps={{
          sx: {
            backgroundColor: snackbar?.severity === 'success' ? 'green' : 'red',
            color: '#fff',
          },
        }}
      />
    </>
  );
};

export default DeleteSimDialog;